import AddIcon from '@mui/icons-material/Add';
import { Box, ButtonBase, IconButton, Skeleton, Typography } from '@mui/material';
import i18n from '../../i18n';
import type { EmployeePermissionClassRow } from './permission-settings.types';

export type PermissionSettingsListPanelProps = {
    /** 角色（员工等级）列表数据 */
    list: EmployeePermissionClassRow[];
    /** 当前选中的角色 `employee_class_id`，未选中为 null */
    selectedId: number | null;
    /** 是否处于加载态，true 时展示骨架屏并禁用新增 */
    loading: boolean;
    /** 选中某一行角色时回调，入参为该行数据 */
    onSelect: (row: EmployeePermissionClassRow) => void;
    /** 点击「新增角色」按钮时回调 */
    onAdd: () => void;
};

/**
 * 权限设置页左侧「角色列表面板」：标题 + 新增按钮 + 列表（含加载骨架屏）。
 * 纯展示组件，状态与请求由父页面持有，仅通过 props 透传。
 */
export default function PermissionSettingsListPanel(props: PermissionSettingsListPanelProps) {
    const { list, selectedId, loading, onSelect, onAdd } = props;

    return (
        <Box className="permission-settings-page__left-column">
            <Box className="permission-settings-page__list-panel">
                <Box className="permission-settings-page__list-header">
                    <Typography component="h2" className="permission-settings-page__list-title">
                        {i18n.t('permission_settings_list_title')}
                    </Typography>
                    <IconButton
                        type="button"
                        size="small"
                        className="permission-settings-page__list-header-add"
                        aria-label={i18n.t('permission_settings_add_role')}
                        disabled={loading}
                        onClick={onAdd}
                    >
                        <AddIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>
                <Box className="permission-settings-page__list-body" aria-busy={loading}>
                    {loading &&
                        Array.from({ length: 6 }).map((_, i) => (
                            <Skeleton
                                key={`permission-settings-list-skeleton-${i}`}
                                variant="rounded"
                                animation="wave"
                                height={32}
                                sx={{
                                    borderRadius: '8px',
                                    width: [0, 3].includes(i % 4) ? '88%' : i % 4 === 1 ? '72%' : '94%',
                                    flexShrink: 0,
                                }}
                            />
                        ))}
                    {!loading &&
                        list.map((row) => {
                            // 当前行的角色 id，可能为 undefined（异常数据兜底用名称作 key）
                            const id = row.employee_class_id;
                            // 是否为当前选中行
                            const selected = id != null && id === selectedId;
                            // 角色名称展示文案
                            const label = String(row.employee_class_name ?? '');
                            return (
                                <ButtonBase
                                    key={id ?? label}
                                    type="button"
                                    className={
                                        selected
                                            ? 'permission-settings-page__list-row permission-settings-page__list-row--selected'
                                            : 'permission-settings-page__list-row'
                                    }
                                    onClick={() => onSelect(row)}
                                >
                                    <Typography component="span" className="permission-settings-page__list-row-label">
                                        {label}
                                    </Typography>
                                </ButtonBase>
                            );
                        })}
                </Box>
            </Box>
        </Box>
    );
}
